import { useState, useMemo, useEffect } from "react";
import { NavLink, useLocation, useNavigate } from "react-router";
import { AnimatePresence, motion } from "motion/react";
import { useTheme } from "next-themes";
import { useAuthStore } from "../../../stores/authStore";
import { logoutUser } from "../../../api/frameworksApi";
import { useHasModuleAccess } from "../../../hooks/usePermission";
import { useCanPerformAnyAction } from "../../../hooks/useRBACPermission";
import type { ModuleType } from "../../../constants/rbac";
import {
  LayoutDashboard,
  BarChart3,
  ShieldCheck,
  BookOpen,
  Layers,
  Package,
  Shield,
  ClipboardCheck,
  FileCheck,
  AlertTriangle,
  Scale,
  X,
  FileText,
  Library,
  Sun,
  Moon,
  LogOut,
  ChevronRight,
  GitMerge,
  PlayCircle,
  Eye,
  Activity,
  FolderTree,
  Building2,
  Shield as ShieldIcon,
  UserCog,
  FileBarChart,
  Database,
  AppWindow,
  Cloud,
  HardDrive,
  Brain,
  SlidersHorizontal,
  Sparkles,
  Network,
  Lightbulb,
  Globe,
  AlertCircle,
  Bell,
  Calendar,
  History,
  ShieldAlert,
  BadgeCheck,
  Clock,
  Wrench,
  Palette,
  Cog,
  FileCog,
  ShieldUser,
  HeartPulse,
  ScanLine,
  Settings,
  PackageCheck,
  PanelLeftClose,
  PanelLeftOpen,
  Users,
} from "lucide-react";
import {
  DiamondIndicator,
  TimelineLine,
} from "./NavIndicator";
import { ShieldLock } from "../../icons";
import { Tip } from "../ui/tooltip";
import type { UserOrgItem, UserGroupItem } from "../../../stores/authStore";
import { shortGroupName } from "../../../utils/formatters";

/* ── Types ──────────────────────────────────────────────── */
interface NavItem {
  label: string;
  path: string;
  icon: React.ElementType;
  module?: ModuleType | string;
  action?: string;
  anyPermissions?: [ModuleType, string][];
}

interface NavSubGroup {
  title: string;
  icon: React.ElementType;
  iconColor: string;
  items: NavItem[];
}

interface NavGroup {
  title: string;
  icon: React.ElementType;
  items: NavItem[];
  subGroups?: NavSubGroup[];
  accent: string;
  accentBg: string;
  iconColor: string;
  directPath?: string;

  module?: string;
}

/* ── Nav data ───────────────────────────────────────────── */
const navGroups: NavGroup[] = [
  {
    title: "Executive Insights",
    icon: Lightbulb,
    accent: "text-[#A78BFA]",
    accentBg: "bg-[#A78BFA]/10",
    iconColor: "#A78BFA",
    items: [],
    directPath: "/dashboard/executive-insights",
  },
  {
    title: "Continuous Compliance",
    icon: ShieldCheck,
    accent: "text-[#388BFD]",
    accentBg: "bg-[#388BFD]/10",
    iconColor: "#388BFD",
    items: [
      { label: "Scheduler", path: "/continuous-compliance/scheduler", icon: Clock },
    ],
    subGroups: [
      {
        title: "Governance",
        icon: Shield,
        iconColor: "#388BFD",
        items: [
          { label: "Content Registry", path: "/continuous-compliance/governance/content-registry", icon: Library, anyPermissions: [["document", "view"], ["evidence", "view"]] },
          { label: "Catalog", path: "/continuous-compliance/governance/catalog", icon: BookOpen, module: "catalog" },
          { label: "Profile", path: "/continuous-compliance/governance/profile", icon: Layers, module: "profile" },
          { label: "Control Mapping", path: "/continuous-compliance/governance/control-mapping", icon: GitMerge, module: "control_mapping" },
        ],
      },
      {
        title: "System",
        icon: Package,
        iconColor: "#34D399",
        items: [
          { label: "Component Definition", path: "/continuous-compliance/system/component-definition", icon: Package, module: "component" },
          { label: "SSP", path: "/continuous-compliance/system/system-security-plan", icon: FileText, module: "ssp" },
        ],
      },
      {
        title: "Assessment",
        icon: ClipboardCheck,
        iconColor: "#F59E0C",
        items: [
          { label: "Assessment Plan", path: "/continuous-compliance/assessment/assessment-plan", icon: ClipboardCheck, module: "assessment_plan" },
          { label: "Assessment Execution", path: "/continuous-compliance/assessment/assessment-execution", icon: PlayCircle, module: "assessment_execution" },
          { label: "Review & Approval", path: "/continuous-compliance/assessment/assessment-review", icon: Eye, module: "assessment_review" },
          { label: "Assessment Result", path: "/continuous-compliance/assessment/assessment-result", icon: FileCheck, module: "assessment_result" },
          { label: "Drift Management", path: "/continuous-compliance/assessment/drift-management", icon: Activity },
        ],
      },
      {
        title: "Risk",
        icon: AlertTriangle,
        iconColor: "#F96C6C",
        items: [
          { label: "POA&M", path: "/continuous-compliance/risk/poam", icon: AlertTriangle, module: "poam" },
        ],
      },
    ],
  },
  {
    title: "Vulnerability Management",
    icon: ShieldAlert,
    accent: "text-[#F59E0B]",
    accentBg: "bg-[#F59E0B]/10",
    iconColor: "#F59E0B",
    module: "it_compliance",
    items: [
      { label: "Overview", path: "/vulnerability-management/overview", icon: BarChart3 },
      { label: "Vulnerability Dashboard", path: "/vulnerability-management/dashboard", icon: LayoutDashboard },
      { label: "Configuration", path: "/vulnerability-management/configuration", icon: Wrench },
      { label: "Schedulers", path: "/vulnerability-management/schedulers", icon: Clock },
    ],
  },
  {
    title: "Configuration Management",
    icon: FileCog,
    accent: "text-[#22d3ee]",
    accentBg: "bg-[#22d3ee]/10",
    iconColor: "#22d3ee",
    module: "it_compliance",
    items: [
      { label: "Overview", path: "/configuration-management/overview", icon: BarChart3 },
      { label: "Configuration Dashboard", path: "/configuration-management/dashboard", icon: LayoutDashboard },
      { label: "Configuration", path: "/configuration-management/configuration", icon: Wrench },
      { label: "Schedulers", path: "/configuration-management/schedulers", icon: Clock },
    ],
  },
  {
    title: "CISO Insights",
    icon: ShieldLock,
    accent: "text-[#8B5CF6]",
    accentBg: "bg-[#8B5CF6]/10",
    iconColor: "#8B5CF6",
    module: "ciso_insights",
    items: [],
    directPath: "/ciso-insights",
  },
  // {
  //   title: "Cloud Governance",
  //   icon: Globe,
  //   accent: "text-[#FB923C]",
  //   accentBg: "bg-[#FB923C]/10",
  //   iconColor: "#FB923C",
  //   items: [
  //     { label: "Architecture Compliance", path: "/architecture-compliance", icon: Network },
  //   ],
  // },
  {
    title: "Privilege Governance Assurance",
    icon: ShieldUser,
    accent: "text-[#FB923C]",
    accentBg: "bg-[#FB923C]/10",
    iconColor: "#FB923C",
    items: [
      { label: "Privilege Access Insights", path: "/privilege-governance-assurance/privilege-access-insights", icon: UserCog },
      { label: "Access Review", path: "/privilege-governance-assurance/access-review", icon: ClipboardCheck },
      { label: "Configuration", path: "/privilege-governance-assurance/account-configuration", icon: SlidersHorizontal },
      { label: "Schedulers", path: "/privilege-governance-assurance/schedulers", icon: Clock },
    ],
  },
  {
    title: "Change Governance Assurance Validation",
    icon: BadgeCheck,
    accent: "text-[#f59e0b]",
    accentBg: "bg-[#f59e0b]/10",
    iconColor: "#f59e0b",
    // directPath: "/change-governance-assurance-validation",
    // items: [],
    items: [
      { label: "Change Governance Assurance Validation", path: "/change-governance-assurance-validation", icon: Eye },
      {
        label: "Change Governance Assurance Readiness",
        path: "/change-governance-assurance-validation/change-request-assessment",
        icon: Sparkles,
        module: "cgar",
      },
      {
        label: "Validation Rules",
        path: "/change-governance-assurance-validation/rules",
        icon: ShieldCheck,
        module: "cgar",
      },
    ],
  },
  {
    title: "Security Tools Operations Center",
    icon: ShieldIcon,
    accent: "text-[#F96C6C]",
    accentBg: "bg-[#F96C6C]/10",
    iconColor: "#F96C6C",
    items: [
      { label: "Asset Coverage", path: "/security-tools/asset-coverage-onboarding", icon: PackageCheck },
      { label: "Scan Coverage", path: "/security-tools/scan-coverage", icon: ScanLine },
      { label: "Agent Health", path: "/security-tools/agent-health", icon: HeartPulse },
      { label: "Configuration", path: "/security-tools/configuration", icon: Settings },
    ],
  },
  {
    title: "Reports",
    icon: FileBarChart,
    accent: "text-[#10b981]",
    accentBg: "bg-[#10b981]/10",
    iconColor: "#10b981",
    items: [],
    directPath: "/report",
  },
  {
    title: "Configuration",
    icon: Settings,
    accent: "text-[#A78BFA]",
    accentBg: "bg-[#A78BFA]/10",
    iconColor: "#A78BFA",
    // Direct item shown alongside the sub-groups (like Audit Log in the design).
    items: [
      { label: "Audit Log", path: "/configuration/audit-log", icon: History },
    ],
    subGroups: [
      {
        title: "CMDB",
        icon: Database,
        iconColor: "#22d3ee",
        items: [
          { label: "Applications", path: "/configuration/cmdb/applications", icon: AppWindow, module: "cmdb" },
          { label: "Devices", path: "/configuration/cmdb/devices", icon: HardDrive, module: "cmdb" },
          { label: "Services", path: "/configuration/cmdb/services", icon: Layers, module: "cmdb" },
          { label: "Cloud Accounts", path: "/configuration/cmdb/cloud-accounts", icon: Cloud, module: "cmdb" },
        ],
      },
      {
        title: "Administration",
        icon: ShieldUser,
        iconColor: "#A78BFA",
        items: [
          { label: "Organization", path: "/configuration/administration/organization", icon: Building2, module: "organization" },
          { label: "Users", path: "/configuration/administration/users", icon: UserCog, module: "user" },
          { label: "Roles", path: "/configuration/administration/roles", icon: ShieldIcon, module: "role" },
          {
            label: "AI Module Configuration",
            path: "/configuration/administration/ai-module-configuration",
            icon: SlidersHorizontal,
            module: "llm_configuration",
          },
          {
            label: "Validation Rules",
            path: "/configuration/administration/validation-rules",
            icon: ShieldCheck,
            module: "validation_rule",
          },
          { label: "Insights Configuration", path: "/configuration/administration/insights-configuration", icon: LayoutDashboard },
          { label: "Schedulers", path: "/configuration/administration/schedulers", icon: Clock },
          { label: "Tools", path: "/configuration/administration/tools", icon: Wrench },
          { label: "Severity Colour Management", path: "/configuration/administration/severity-colors", icon: Palette },
          { label: "Alert Calendar", path: "/configuration/administration/alert-calendar", icon: Calendar },
        ],
      },
      {
        title: "Settings",
        icon: SlidersHorizontal,
        iconColor: "#A78BFA",
        items: [
          { label: "Directory Configuration", path: "/configuration/settings/directory-configuration", icon: FolderTree },
          { label: "License Management", path: "/configuration/administration/license-management", icon: Scale },
          {
            label: "LLM Configuration",
            path: "/configuration/administration/llm-configuration",
            icon: Brain,
            module: "llm_configuration",
          },

        ],
      },
    ],
  },
];

/* ── Page labels ────────────────────────────────────────── */
/** Every navigable path with the label this menu shows for it, longest path first. */
const navLabelsByPath: Array<[string, string]> = navGroups
  .flatMap<[string, string]>((group) => [
    ...(group.directPath
      ? ([[group.directPath, group.title]] as Array<[string, string]>)
      : []),
    ...group.items.map((item): [string, string] => [item.path, item.label]),
    ...(group.subGroups ?? []).flatMap((subGroup) =>
      subGroup.items.map((item): [string, string] => [item.path, item.label]),
    ),
  ])
  .sort(([a], [b]) => b.length - a.length);

/**
 * The menu label for a route, or `null` when no menu entry claims it.
 *
 * Exported so the assistant panel names a page exactly as this menu does — one table, so the two can
 * never disagree about what a page is called. Longest match wins, and a match must end at a path
 * segment, so a deep link (`…/catalog/53`) still resolves to "Catalog" while a sibling route never
 * borrows another's label.
 */
export function pageLabelForPath(path: string | null | undefined): string | null {
  if (!path) return null;
  const normalized = path.split("?")[0].split("#")[0].replace(/\/+$/, "").toLowerCase();
  if (!normalized) return null;
  const match = navLabelsByPath.find(([candidate]) => {
    const prefix = candidate.toLowerCase();
    return normalized === prefix || normalized.startsWith(`${prefix}/`);
  });
  return match ? match[1] : null;
}

/* ── RegO Logo ─────────────────────────────────────────── */
function RegoLogo({
  collapsed,
  className,
}: {
  collapsed: boolean;
  className?: string;
}) {
  return (
    <div
      className={
        className !== undefined
          ? className
          : "flex items-center justify-start  px-2 py-1 "
      }
    >
      {/* Shield icon */}
      <svg
        width="28"
        height="28"
        viewBox="0 0 32 32"
        fill="none"
        className="flex-shrink-0"
      >
        <defs>
          <linearGradient
            id="shield-grad-1"
            x1="0%"
            y1="100%"
            x2="100%"
            y2="0%"
          >
            <stop offset="0%" stopColor="#A18DC2" />
            <stop offset="50%" stopColor="#BAC3E3" />
            <stop offset="100%" stopColor="#A18DC2" />
          </linearGradient>
          <linearGradient
            id="shield-grad-2"
            x1="100%"
            y1="0%"
            x2="0%"
            y2="100%"
          >
            <stop offset="0%" stopColor="#CE7EB3" />
            <stop offset="50%" stopColor="#D6A0C8" />
            <stop offset="100%" stopColor="#A090C4" />
          </linearGradient>
        </defs>
        {/* Left shield half */}
        <path
          d="M16 30C11.5 28.6 8 24.8 6.5 20.5C5.6 18.2 5.1 15.7 4.8 13.2C4.8 12.6 4.4 6.4 4.7 6.4C8.7 6.3 12.6 5.3 16 3"
          fill="url(#shield-grad-1)"
        />
        {/* Right shield half */}
        <path
          d="M16 30C20.5 28.6 24 24.8 25.5 20.5C26.4 18.2 26.9 15.7 27.2 13.2C27.2 12.6 27.6 6.4 27.3 6.4C23.3 6.3 19.4 5.3 16 3"
          fill="url(#shield-grad-2)"
        />
        {/* Left inner */}
        <path
          d="M4.7 6.4C4.7 6.4 7.2 6.4 10 5.7C10 5.7 10.1 18 16 21.5V30C16 30 4.6 27.7 4.6 8C4.6 6.3 4.7 6.4 4.7 6.4Z"
          fill="url(#shield-grad-1)"
          opacity="0.7"
        />
        {/* Right inner */}
        <path
          d="M27.3 6.4C26.7 6.3 24.9 6.4 22 5.7C22 5.7 21.9 18 16 21.5V30C16 30 27.5 27.4 27.4 8C27.4 7.9 27.4 7.8 27.4 7.7C27.4 6.4 27.3 6.4 27.3 6.4Z"
          fill="url(#shield-grad-2)"
          opacity="0.7"
        />
        {/* Checkmark */}
        <path
          d="M22.5 11.5L22.6 11.7C21.2 13 19.9 14.4 18.7 15.9C17 18.3 15.4 21 14 23.8H13.8L9.8 18.9L9.9 18.7H10L13.4 20.6C14.8 18.7 16.3 16.8 18 15.2C18.8 14.4 19.6 13.7 20.4 13C21.2 12.4 21.9 11.9 22.5 11.5Z"
          fill="white"
        />
      </svg>

      {/* RegO text */}
      {!collapsed && (
        <motion.div
          initial={{ opacity: 0, x: -8 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -8 }}
          transition={{ duration: 0.2, delay: 0.1 }}
          className="flex-shrink-0"
        >
          <svg width="80" height="24" viewBox="0 0 80 24" fill="none">
            <defs>
              <linearGradient
                id="text-grad"
                x1="0%"
                y1="100%"
                x2="100%"
                y2="0%"
              >
                <stop offset="0%" stopColor="#A1A6D3" />
                <stop offset="50%" stopColor="#9FA0CF" />
                <stop offset="80%" stopColor="#A097C9" />
                <stop offset="100%" stopColor="#A089C0" />
              </linearGradient>
            </defs>
            <text
              x="0"
              y="20"
              fill="url(#text-grad)"
              fontFamily="'Montserrat', sans-serif"
              fontWeight="600"
              fontSize="20"
              letterSpacing="-0.5"
            >
              RegO
            </text>
          </svg>
        </motion.div>
      )}
    </div>
  );
}

/* ── Animation constants ────────────────────────────────── */
const SIDEBAR_W_OPEN = 260;
const SIDEBAR_W_CLOSED = 64;
const EASE = [0.25, 0.46, 0.45, 0.94] as const;
const DURATION = 0.3;

/* ── Nav Item Component ─────────────────────────────────── */
function NavItemComponent({
  item,
  active,
  groupIconColor,
  onItemClick,
  theme,
}: {
  item: NavItem;
  active: boolean;
  groupIconColor: string;
  onItemClick: () => void;
  theme?: string;
}) {
  const hasModuleAccess = useHasModuleAccess(item.module || "");
  const hasAnyPermission = useCanPerformAnyAction(item.anyPermissions ?? []);

  if (
    item.anyPermissions?.length
      ? !hasAnyPermission
      : item.module && !hasModuleAccess
  ) {
    return null;
  }

  const isLight = theme === "light";
  const activeBg = isLight ? "bg-slate-200/60" : "bg-white/[0.06]";
  const activeText = isLight
    ? "text-slate-900 font-semibold"
    : "text-white font-semibold";
  const inactiveBg = isLight
    ? "hover:bg-slate-200/40"
    : "hover:bg-white/[0.04]";
  const inactiveText = isLight
    ? "text-slate-600 hover:text-slate-800"
    : "text-white/60 hover:text-white/80";

  return (
    <NavLink
      to={item.path}
      onClick={onItemClick}
      className={`
        flex items-center gap-2.5 mx-1.5 px-2.5 py-1.5 rounded-md text-[13px]
        transition-colors duration-150 group relative
        ${active
          ? `${activeBg} ${activeText}`
          : `${inactiveText} ${inactiveBg} font-normal`
        }
      `}
    >
      <DiamondIndicator active={active} color={groupIconColor} />
      <Tip label={item.label} side="right" align="center" className="truncate whitespace-nowrap flex-1 min-w-0">
        <span className="truncate whitespace-nowrap block">{item.label}</span>
      </Tip>
      {active && (
        <span
          className="ml-auto size-1.5 rounded-full flex-shrink-0"
          style={{ background: groupIconColor }}
        />
      )}
    </NavLink>
  );
}

/* ── Props ───────────────────────────────────────────────── */
interface SidebarProps {
  collapsed: boolean;
  onExpand: () => void;
  onCollapse: () => void;
  mobileOpen: boolean;
  onMobileClose: () => void;
}

export function Sidebar({
  collapsed,
  onExpand,
  onCollapse,
  mobileOpen,
  onMobileClose,
}: SidebarProps) {
  const { theme } = useTheme();
  const location = useLocation();
  const [isSidebarHovered, setIsSidebarHovered] = useState(false);

  const isActive = (path: string) =>
    path === "/" || path === "/dashboard"
      ? location.pathname === path
      : location.pathname === path || location.pathname.startsWith(`${path}/`);

  const groupHasActive = (g: NavGroup) => {
    if (g.items.some((i) => isActive(i.path))) return true;
    if (g.subGroups) return g.subGroups.some((sg) => sg.items.some((i) => isActive(i.path)));
    return false;
  };

  const sidebarBg = theme === "light" ? "#FFFFFF" : "#0D1322";
  const sidebarBorder = theme === "light" ? "#E2E8F0" : "#18233F";

  return (
    <>
      {/* Desktop - motion width */}
      <motion.aside
        animate={{ width: collapsed ? SIDEBAR_W_CLOSED : SIDEBAR_W_OPEN }}
        transition={{ duration: DURATION, ease: EASE }}
        onMouseEnter={() => setIsSidebarHovered(true)}
        onMouseLeave={() => setIsSidebarHovered(false)}
        className="hidden lg:flex flex-col flex-shrink-0 h-full"
        style={{
          width: collapsed ? SIDEBAR_W_CLOSED : SIDEBAR_W_OPEN,
          overflow: "hidden",
          background: sidebarBg,
          borderRight: `1px solid ${sidebarBorder}`,
        }}
      >
        <SidebarContent
          collapsed={collapsed}
          onExpand={onExpand}
          onCollapse={onCollapse}
          isActive={isActive}
          groupHasActive={groupHasActive}
          isSidebarHovered={isSidebarHovered}
        />
      </motion.aside>

      {/* Mobile drawer */}
      <aside
        className={`fixed inset-y-0 left-0 z-30 flex flex-col h-full lg:hidden overflow-hidden
          transition-transform duration-300 ease-in-out
          ${mobileOpen ? "translate-x-0" : "-translate-x-full"}`}
        style={{
          width: SIDEBAR_W_OPEN,
          background: sidebarBg,
          borderRight: `1px solid ${sidebarBorder}`,
        }}
      >
        {mobileOpen && (
          <SidebarContent
            collapsed={false}
            onExpand={() => { }}
            onCollapse={onMobileClose}
            isActive={isActive}
            groupHasActive={groupHasActive}
            isMobile
            onMobileClose={onMobileClose}
          />
        )}
      </aside>
    </>
  );
}

/* ── Alert Calendar ──────────────────────────────────────── */
/* Icon only, and it sits with the account row rather than under
   Administration — it is a day-to-day view, not a configuration screen. */
function AlertCalendarButton({
  theme,
  active,
  onClick,
}: {
  theme: string | undefined;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <Tip label="Alert Calendar" side="right" align="center">
      <button
        onClick={onClick}
        aria-label="Alert Calendar"
        aria-current={active ? "page" : undefined}
        /* No fill — violet throughout, brightening slightly on hover. */
        className="flex size-8 flex-none items-center justify-center rounded-lg opacity-90 transition-opacity duration-150 hover:opacity-100"
      >
        {/* Neither lucide nor react-icons ships a calendar-with-bell, so it is
            composed: the bell sits in a punched-out notch at the corner, the way
            lucide's own calendar-* variants are drawn. */}
        <span className="relative inline-flex" style={{ color: "#A78BFA" }}>
          <Calendar className="size-[18px]" />
          <span
            className="absolute -bottom-[3px] -right-[3px] flex items-center justify-center rounded-full p-[1.5px]"
            style={{ background: theme === "light" ? "#FFFFFF" : "#0D1322" }}
          >
            <Bell className="size-[10px]" />
          </span>
        </span>
      </button>
    </Tip>
  );
}

/* ── Inner content ───────────────────────────────────────── */
function SidebarContent({
  collapsed,
  onExpand,
  onCollapse,
  isActive,
  groupHasActive,
  isMobile = false,
  onMobileClose,
  isSidebarHovered = false,
}: {
  collapsed: boolean;
  onExpand: () => void;
  onCollapse: () => void;
  isActive: (path: string) => boolean;
  groupHasActive: (g: NavGroup) => boolean;
  isMobile?: boolean;
  onMobileClose?: () => void;
  isSidebarHovered?: boolean;
}) {
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const loggedInUser = user?.displayName || user?.username || "";
  const userRole = user?.roles?.[0] || "Compliance User";

  const userOrganizations: UserOrgItem[] = useMemo(() => {
    if (user?.organizations && Array.isArray(user.organizations) && user.organizations.length > 0) {
      return user.organizations.map((o) => (typeof o === "string" ? { name: o } : o));
    }
    if (user?.orgName) {
      return [{ id: user.orgId || undefined, name: user.orgName }];
    }
    return [];
  }, [user?.organizations, user?.orgName, user?.orgId]);

  const userGroups: UserGroupItem[] = useMemo(() => {
    if (!user?.groups || !Array.isArray(user.groups)) return [];
    return user.groups.map((g) =>
      typeof g === "string" ? { name: shortGroupName(g), dn: g } : g,
    );
  }, [user?.groups]);

  const location = useLocation();

  const [openGroups, setOpenGroups] = useState<Set<string>>(
    () => new Set(navGroups.filter(groupHasActive).map((g) => g.title)),
  );

  // Track which sub-groups are expanded (key: "GroupTitle:SubGroupTitle")
  const [openSubGroups, setOpenSubGroups] = useState<Set<string>>(() => {
    const active = new Set<string>();
    for (const group of navGroups) {
      if (group.subGroups) {
        for (const sg of group.subGroups) {
          if (sg.items.some((i) => isActive(i.path))) {
            active.add(`${group.title}:${sg.title}`);
          }
        }
      }
    }
    return active;
  });

  // Keep open groups and sub-groups synchronized reactively on navigation
  useEffect(() => {
    for (const group of navGroups) {
      if (groupHasActive(group)) {
        setOpenGroups((prev) => {
          if (prev.has(group.title)) return prev;
          const next = new Set(prev);
          next.add(group.title);
          return next;
        });
      }
      if (group.subGroups) {
        for (const sg of group.subGroups) {
          if (sg.items.some((i) => isActive(i.path))) {
            const key = `${group.title}:${sg.title}`;
            setOpenSubGroups((prev) => {
              if (prev.has(key)) return prev;
              const next = new Set(prev);
              next.add(key);
              return next;
            });
          }
        }
      }
    }
  }, [location.pathname, isActive, groupHasActive]);

  const isItemSelected = (item: NavItem, siblings: NavItem[]) =>
    isActive(item.path) &&
    !siblings.some(
      (s) => s.path !== item.path && s.path.length > item.path.length && isActive(s.path),
    );

  const [showUserMenu, setShowUserMenu] = useState(false);
  const hasItCompliance = useHasModuleAccess("it_compliance");
  const hasCisoInsights = useHasModuleAccess("ciso_insights");
  const moduleAccess: Record<string, boolean> = {
    it_compliance: hasItCompliance,
    ciso_insights: hasCisoInsights,
  };
  const visibleNavGroups = navGroups.filter(
    (g) => !g.module || moduleAccess[g.module] !== false,
  );

  // Theme-aware colors
  const borderColor = theme === "light" ? "#E2E8F0" : "#223159";
  const groupBgColor = theme === "light" ? "#F0F4F8" : "#242A3A";
  const menuBgColor = theme === "light" ? "#FFFFFF" : "#0D1322";
  const menuItemBgColor = theme === "light" ? "#F0F4F8" : "#1A2236";

  const isOpen = !collapsed;

  const handleGroupClick = (group: NavGroup) => {
    if (collapsed) {
      onExpand();
      setOpenGroups(new Set([group.title]));
      return;
    }
    setOpenGroups((prev) => {
      const next = new Set(prev);
      next.has(group.title)
        ? next.delete(group.title)
        : next.add(group.title);
      return next;
    });
  };

  const toggleSubGroup = (key: string) => {
    setOpenSubGroups((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const isGroupOpen = (group: NavGroup) => {
    if (collapsed) return false;
    return openGroups.has(group.title);
  };

  const handleItemClick = () => {
    if (isMobile) {
      onMobileClose?.();
    } else {
      onCollapse();
    }
  };

  const handleDirectGroupClick = (group: NavGroup) => {
    if (isMobile) {
      onMobileClose?.();
      return;
    }
    setOpenGroups(new Set([group.title]));
    onCollapse();
  };

  return (
    <div className="flex flex-col h-full min-w-0 overflow-hidden">
      {/* ── Brand ────────────────────────────────────────── */}
      <div
        className={`flex items-center h-[52px] flex-shrink-0 overflow-hidden ${collapsed ? "justify-center px-2" : "justify-between px-3"
          }`}
        style={{ borderBottom: `1px solid ${borderColor}` }}
      >
        {collapsed ? (
          <div className="flex items-center justify-between w-full px-1.5">
            <div className="flex items-center justify-center flex-shrink-0">
              <RegoLogo collapsed={true} className="flex items-center justify-center p-0" />
            </div>
            <Tip label="Expand sidebar" side="right">
              <button
                onClick={onExpand}
                className={`p-1.5 rounded-lg transition-colors flex items-center justify-center flex-shrink-0
                  ${theme === "light"
                    ? "text-slate-400 hover:text-[#388BFD] hover:bg-slate-100"
                    : "text-white/40 hover:text-[#388BFD] hover:bg-white/10"
                  }`}
                aria-label="Expand sidebar"
              >
                <PanelLeftOpen className="size-4" />
              </button>
            </Tip>
          </div>
        ) : (
          <>
            <RegoLogo collapsed={false} />
            {isMobile ? (
              <button
                onClick={onMobileClose}
                className={`ml-auto p-1.5 rounded-md transition-colors
                  ${theme === "light" ? "text-slate-400 hover:text-slate-600 hover:bg-slate-200/40" : "text-white/30 hover:text-white/60 hover:bg-white/5"}`}
                aria-label="Close sidebar"
              >
                <X className="size-4" />
              </button>
            ) : (
              <Tip label="Collapse sidebar" side="bottom">
                <button
                  onClick={onCollapse}
                  className={`p-1.5 rounded-lg transition-colors flex items-center justify-center ml-auto
                    ${theme === "light"
                      ? "text-slate-400 hover:text-[#388BFD] hover:bg-slate-100"
                      : "text-white/40 hover:text-[#388BFD] hover:bg-white/10"
                    }`}
                  aria-label="Collapse sidebar"
                >
                  <PanelLeftClose className="size-4.5" />
                </button>
              </Tip>
            )}
          </>
        )}
      </div>

      {/* ── Nav groups ─────────────────────────────────── */}
      <nav
        className="flex-1 min-h-0 overflow-y-auto scrollbar-none px-2 py-1.5 space-y-1"
        style={{ scrollbarWidth: "none" }}
      >
        {visibleNavGroups.map((group) => {
          const GIcon = group.icon;
          const open = isGroupOpen(group);
          const hasActive = groupHasActive(group);

          // For groups with subGroups, always show them
          const visibleItemsCount = group.items.filter(
            (item) => !item.module || useHasModuleAccess(item.module),
          ).length;

          if (!group.directPath && !group.subGroups && visibleItemsCount === 0) return null;

          const directActive = group.directPath ? isActive(group.directPath) : false;

          return (
            <div key={group.title}>
              <div
                className="relative rounded-md transition-colors duration-150"
                style={{
                  background: directActive || open || hasActive ? groupBgColor : "transparent",
                }}
              >
                {/* Group header */}
                {group.directPath ? (
                  <Tip
                    label={collapsed ? group.title : undefined}
                    side="right"
                    align="center"
                    className="w-full flex justify-center"
                  >
                    <NavLink aria-label={group.title}
                      to={group.directPath}
                      onClick={() => handleDirectGroupClick(group)}
                      className={`w-full flex items-center gap-2.5 px-2.5 py-1.5 cursor-pointer relative ${collapsed ? "justify-center" : ""
                        }`}
                    >
                    <span
                      className={`flex items-center justify-center flex-shrink-0 size-[16px] rounded transition-colors ${directActive ? group.accentBg : ""
                        }`}
                    >
                      <GIcon
                        className="size-[14px] transition-colors"
                        style={{
                          color: directActive
                            ? group.iconColor
                            : theme === "light"
                              ? "rgba(0,0,0,0.35)"
                              : "rgba(255,255,255,0.35)",
                        }}
                      />
                    </span>

                    {isOpen && (
                      <Tip label={group.title} side="right" align="center" className="truncate whitespace-nowrap flex-1 min-w-0">
                        <motion.span
                          initial={{ opacity: 0, x: -6 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: -6 }}
                          transition={{ duration: 0.15, delay: 0.1 }}
                          className="flex-1 text-left text-[13px] font-semibold truncate whitespace-nowrap block"
                          style={{
                            color: directActive
                              ? group.iconColor
                              : theme === "light"
                                ? "rgba(0,0,0,0.5)"
                                : "rgba(255,255,255,0.5)",
                          }}
                        >
                          {group.title}
                        </motion.span>
                      </Tip>
                    )}
                    </NavLink>
                  </Tip>
                ) : (
                  <Tip
                    label={collapsed ? group.title : undefined}
                    side="right"
                    align="center"
                    className="w-full flex justify-center"
                  >
                    <button aria-label={group.title}
                      onClick={() => handleGroupClick(group)}
                      className={`w-full flex items-center gap-2.5 px-2.5 py-1.5 cursor-pointer relative ${collapsed ? "justify-center" : ""
                        }`}
                    >
                    <span
                      className={`flex items-center justify-center flex-shrink-0 size-[16px] rounded transition-colors ${hasActive || open ? group.accentBg : ""
                        }`}
                    >
                      <GIcon
                        className="size-[14px] transition-colors"
                        style={{
                          color:
                            hasActive || open
                              ? group.iconColor
                              : theme === "light"
                                ? "rgba(0,0,0,0.35)"
                                : "rgba(255,255,255,0.35)",
                        }}
                      />
                    </span>

                    {isOpen && (
                      <Tip label={group.title} side="right" align="center" className="truncate whitespace-nowrap flex-1 min-w-0">
                        <motion.span
                          initial={{ opacity: 0, x: -6 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: -6 }}
                          transition={{ duration: 0.15, delay: 0.1 }}
                          className="flex-1 text-left text-[13px] font-semibold truncate whitespace-nowrap block"
                          style={{
                            color:
                              hasActive || open
                                ? group.iconColor
                                : theme === "light"
                                  ? "rgba(0,0,0,0.5)"
                                  : "rgba(255,255,255,0.5)",
                          }}
                        >
                          {group.title}
                        </motion.span>
                      </Tip>
                    )}
                    </button>
                  </Tip>
                )}

                {/* Sub-items or Sub-groups */}
                {!group.directPath && (
                  <AnimatePresence initial={false}>
                    {open && isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.28, ease: [0.4, 0, 0.2, 1] }}
                        style={{ overflow: "hidden", willChange: "height" }}
                      >
                        {group.subGroups ? (
                          /* ── Nested sub-groups (e.g. Continuous Compliance) ── */
                          <div className="pb-1">
                            {group.subGroups.map((subGroup) => {
                              const SubIcon = subGroup.icon;
                              const sgKey = `${group.title}:${subGroup.title}`;
                              const sgOpen = openSubGroups.has(sgKey);
                              const sgHasActive = subGroup.items.some((i) => isActive(i.path));
                              return (
                                <div key={subGroup.title} className="mb-0.5">
                                  {/* Sub-group header */}
                                  <button
                                    onClick={() => toggleSubGroup(sgKey)}
                                    className={`w-full flex items-center gap-2 mx-1.5 px-2.5 py-1.5 rounded-md text-[12px] font-semibold transition-colors duration-150 ${sgHasActive || sgOpen
                                      ? theme === "light"
                                        ? "text-slate-700"
                                        : "text-white/80"
                                      : theme === "light"
                                        ? "text-slate-500 hover:text-slate-700"
                                        : "text-white/40 hover:text-white/60"
                                      }`}
                                  >
                                    <SubIcon
                                      className="size-3 flex-shrink-0"
                                      style={{
                                        color: sgHasActive || sgOpen ? subGroup.iconColor : undefined,
                                      }}
                                    />
                                    <Tip label={subGroup.title} side="right" align="center" className="truncate whitespace-nowrap flex-1 min-w-0 text-left">
                                      <span className="truncate whitespace-nowrap block">{subGroup.title}</span>
                                    </Tip>
                                    <ChevronRight
                                      className={`ml-auto size-3 flex-shrink-0 transition-transform duration-200 ${sgOpen ? "rotate-90" : ""
                                        }`}
                                      style={{
                                        color: sgHasActive || sgOpen ? subGroup.iconColor : undefined,
                                      }}
                                    />
                                  </button>

                                  {/* Sub-group items */}
                                  <AnimatePresence initial={false}>
                                    {sgOpen && (
                                      <motion.div
                                        initial={{ height: 0, opacity: 0 }}
                                        animate={{ height: "auto", opacity: 1 }}
                                        exit={{ height: 0, opacity: 0 }}
                                        transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
                                        style={{ overflow: "hidden" }}
                                      >
                                        <div className="pb-0.5 relative pl-2">
                                          {subGroup.items.length > 1 && (
                                            <TimelineLine centerX={31} top={14} bottom={14} />
                                          )}
                                          {subGroup.items.map((item) => {
                                            const active = isItemSelected(item, subGroup.items);
                                            return (
                                              <NavItemComponent
                                                key={item.path}
                                                item={item}
                                                active={active}
                                                groupIconColor={subGroup.iconColor}
                                                onItemClick={handleItemClick}
                                                theme={theme}
                                              />
                                            );
                                          })}
                                        </div>
                                      </motion.div>
                                    )}
                                  </AnimatePresence>
                                </div>
                              );
                            })}
                            {/* Direct items shown alongside the sub-groups (e.g. Audit Log) —
                                rendered with their own icon to match the sub-group header rows. */}
                            {group.items.map((item) => {
                              const active = isActive(item.path);
                              const ItemIcon = item.icon;
                              return (
                                <NavLink
                                  key={item.path}
                                  to={item.path}
                                  onClick={handleItemClick}
                                  className={`w-full flex items-center gap-2 mx-1.5 px-2.5 py-1.5 rounded-md text-[12px] font-semibold transition-colors duration-150 ${active
                                    ? theme === "light"
                                      ? "text-slate-700"
                                      : "text-white/80"
                                    : theme === "light"
                                      ? "text-slate-500 hover:text-slate-700"
                                      : "text-white/40 hover:text-white/60"
                                    }`}
                                >
                                  <ItemIcon
                                    className="size-3 flex-shrink-0"
                                    style={{ color: active ? group.iconColor : undefined }}
                                  />
                                  <Tip label={item.label} side="right" align="center" className="truncate whitespace-nowrap flex-1 min-w-0">
                                    <span className="truncate whitespace-nowrap block">{item.label}</span>
                                  </Tip>
                                </NavLink>
                              );
                            })}
                          </div>
                        ) : (
                          /* ── Flat items ── */
                          <div className="pb-1 relative">
                            {group.items.length > 1 && (
                              <TimelineLine centerX={23} top={14} bottom={14} />
                            )}
                            {group.items.map((item) => {
                              const active = isItemSelected(item, group.items);
                              return (
                                <NavItemComponent
                                  key={item.path}
                                  item={item}
                                  active={active}
                                  groupIconColor={group.iconColor}
                                  onItemClick={handleItemClick}
                                  theme={theme}
                                />
                              );
                            })}
                          </div>
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>
                )}
              </div>
            </div>
          );
        })}
      </nav>

      {/* Collapsed rail has no room beside the avatar, so the icon takes its
          own centred row. */}
      {collapsed && (
        <div
          className="flex flex-shrink-0 justify-center py-2"
          style={{ borderTop: `1px solid ${borderColor}` }}
        >
          <AlertCalendarButton
            theme={theme}
            active={isActive("/configuration/administration/alert-calendar")}
            onClick={() => {
              navigate("/configuration/administration/alert-calendar");
              handleItemClick();
            }}
          />
        </div>
      )}

      {/* ── User footer ────────────────────────────────── */}
      <div
        className="flex flex-shrink-0 items-center"
        style={{ borderTop: `1px solid ${borderColor}` }}
      >
        <button
          onClick={() => {
            if (collapsed) {
              /* The collapsed rail has no room for the menu panel, so the profile
                 doubles as the expander: open the sidebar and show the menu in
                 the same click (the panel mounts as soon as the rail widens). */
              onExpand();
            }
            setShowUserMenu((p) => !p);
          }}
          title={collapsed ? "Open sidebar" : undefined}
          className={`min-w-0 flex-1 flex items-center gap-2.5 px-3 py-3 overflow-hidden transition-colors duration-150
            ${theme === "light" ? "hover:bg-slate-200/40" : "hover:bg-white/[0.04]"} ${showUserMenu ? (theme === "light" ? "bg-slate-200/40" : "bg-white/[0.04]") : ""}`}
        >
          <div className="size-9 rounded-full bg-[#388BFD]/20 flex-shrink-0 flex items-center justify-center ring-2 ring-[#388BFD]/20">
            <span className="text-xs font-black text-[#388BFD] ">
              {loggedInUser.charAt(0)}
              {loggedInUser.split(" ")[1]?.charAt(0) ?? ""}
            </span>
          </div>

          {isOpen && (
            <motion.div
              initial={{ opacity: 0, x: -6 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -6 }}
              transition={{ duration: 0.15, delay: 0.1 }}
              className="flex-1 min-w-0 flex items-center gap-2"
            >
              <div className="flex-1 min-w-0 text-left">
                <p
                  className={`text-[11px] font-bold truncate whitespace-nowrap ${theme === "light" ? "text-slate-900" : "text-white"}`}
                >
                  {loggedInUser}
                </p>
                <p
                  className={`text-[9px] truncate whitespace-nowrap ${theme === "light" ? "text-slate-500" : "text-white/30"}`}
                >
                  {userRole}
                </p>
              </div>
              <ChevronRight
                className={`flex-shrink-0 size-3 transition-all duration-200
                ${showUserMenu ? "rotate-90 text-[#388BFD]" : theme === "light" ? "text-slate-400" : "text-white/25"}`}
              />
            </motion.div>
          )}
        </button>

        {isOpen && (
          <div className="flex-none pl-1.5 pr-3">
            <AlertCalendarButton
              theme={theme}
              active={isActive("/configuration/administration/alert-calendar")}
              onClick={() => {
                navigate("/configuration/administration/alert-calendar");
                handleItemClick();
              }}
            />
          </div>
        )}
      </div>

      {/* ── User Menu Panel ──────────────────────────── */}
      <AnimatePresence>
        {showUserMenu && isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.24, ease: "easeInOut" }}
            className="flex-shrink-0 overflow-hidden"
            style={{ borderTop: `1px solid ${borderColor}` }}
          >
            <div style={{ background: menuBgColor }}>
              {/* ── User Profile Details (Organization & Group) ──────── */}
              <div
                className="px-3 py-2.5 space-y-2.5"
                style={{ borderBottom: `1px solid ${borderColor}` }}
              >
                {/* Organization */}
                <div className="space-y-1">
                  <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-white/40">
                    <Building2 className="size-3 text-[#388BFD] flex-shrink-0" />
                    <span>Organization</span>
                  </div>
                  {userOrganizations.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {userOrganizations.map((org, idx) => (
                        <span
                          key={org.id || org.name || idx}
                          title={org.name}
                          className="inline-flex items-center text-[11px] px-2 py-0.5 rounded font-medium truncate max-w-full bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-300 border border-amber-200/50 dark:border-amber-500/20"
                        >
                          {org.name}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-[11px] text-slate-400 dark:text-white/30 italic pl-0.5">
                      No organization assigned
                    </p>
                  )}
                </div>
                <div className="space-y-1">
                  <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-white/40">
                    <Users className="size-3 text-teal-600 dark:text-teal-400 flex-shrink-0" />
                    <span>Group</span>
                  </div>
                  {userGroups.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {userGroups.map((grp, idx) => (
                        <Tip key={idx} label={grp.dn || grp.name} side="top">
                          <span
                            className="inline-flex items-center text-[11px] px-2 py-0.5 rounded font-medium truncate max-w-[210px] bg-teal-50 dark:bg-teal-500/10 text-teal-700 dark:text-teal-300 border border-teal-200/50 dark:border-teal-500/20 cursor-default"
                          >
                            {grp.name}
                          </span>
                        </Tip>
                      ))}
                    </div>
                  ) : (
                    <p className="text-[11px] text-slate-400 dark:text-white/30 italic pl-0.5">
                      No group assigned
                    </p>
                  )}
                </div>
              </div>

              {/* Theme toggle */}
              <div className="px-2 py-1.5">
                <div
                  className="flex items-center gap-2 px-2.5 py-2 rounded-lg"
                  style={{
                    background: menuItemBgColor,
                    border: `1px solid ${borderColor}`,
                  }}
                >
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    {theme === "dark" ? (
                      <Moon className="size-3.5 text-[#388BFD] flex-shrink-0" />
                    ) : (
                      <Sun className="size-3.5 text-amber-500 flex-shrink-0" />
                    )}
                    <span
                      className={`text-[11px] font-semibold truncate whitespace-nowrap ${theme === "light" ? "text-slate-900" : "text-white"}`}
                    >
                      {theme === "dark" ? "Dark Mode" : "Light Mode"}
                    </span>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setTheme(theme === "dark" ? "light" : "dark");
                    }}
                    className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent
                      transition-colors duration-200 focus:outline-none
                      ${theme === "dark" ? "bg-[#388BFD]" : "bg-[#388BFD]"}`}
                  >
                    <span
                      className={`pointer-events-none inline-block size-4 rounded-full bg-white shadow-md
                        transform transition-transform duration-200
                        ${theme === "dark" ? "translate-x-4" : "translate-x-0"}`}
                    />
                  </button>
                </div>
              </div>

              <div
                className="px-2 py-1.5"
                style={{ borderTop: `1px solid ${borderColor}` }}
              >
                <button
                  onClick={() => {
                    setShowUserMenu(false);
                    // Always land on /login, even if the logout request fails.
                    logoutUser().catch(() => { }).finally(() => navigate("/login"));
                  }}
                  className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg
                    transition-colors group
                    ${theme === "light" ? "text-red-600 hover:bg-red-100" : "text-[#F96C6C] hover:bg-[#F96C6C]/10"}`}
                >
                  <LogOut className="size-3.5 flex-shrink-0" />
                  <span className="text-xs font-medium whitespace-nowrap">
                    Log out
                  </span>
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
